import { message } from 'antd';
import lodash from 'lodash';
import { useEffect, useState } from 'react';
import { UseQueryResult, useMutation, useQuery } from '@tanstack/react-query';
import { QueryObserverOptions } from '@tanstack/react-query/types/core/types';
import { UseQueryOptions } from '@tanstack/react-query/types/react/types';
import { useSelector } from 'react-redux';

import { sortByType } from '@src/common/constant/enum';
import {
  DimensionQueryBody,
  IBlockEventData,
  IBlockVideoEventData,
} from '@src/common/types/empTypes';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import { removeEmptyFilters } from '@src/emp-3.0/utils';
import {
  refetchDimensionRelated,
  refetchFlowRelated,
  refetchMappedEventRelated,
  refetchMetricRelated,
} from '@src/emp-3.0/utils/refetch';
import { queryClient } from '@src/routers/constants';
import { trackQueryInfo } from '@src/sensor';
import commonSelector from '@store/common/selectors';
import METRIC from '@utils/metricKeys';

import {
  IGetMappedEvents,
  deleteDateTransform,
  deleteMappedEvent,
  deleteNetworkConfigures,
  postDeploy,
  getAllDataTransform,
  getBlockEventById,
  getBlockEvents,
  getBlockVideoEvents,
  getDataTransformByNameId,
  getDimensionData,
  getEventCategory,
  getMappedEventById,
  getMappedEventDetail,
  getMappedEventManagementList,
  getMappedEventMetadataEventName,
  getMappedEvents,
  getMappedStatusEvent,
  getNetworkConfigures,
  getPresets,
  postBlockEvents,
  postBlockVideoEvents,
  postDataTransform,
  postMappedEvents,
  postNetworkConfigures,
  postQuickMap,
  postUnblockEvents,
  postUnblockVideoEvents,
  updateDataTransform,
  updateMappedEvent,
  updateNetworkConfigures,
  reorderNetworkConfigures,
  getDeployCandidateList,
  postDeployList,
  getClickTrackingData,
  postClickTracking,
  getSessionReplayData,
  deleteCollectAttr,
  getWorkbenchDeployInfo,
  exportWorkbenchToProduction,
  postWorkbenchDeploy,
  postSessionReplay,
  getDeployTasks,
} from './query';
import { INETWORK_CONFIGURES } from './types';
import { getValidTimeRange } from '@src/emp-3.0/api/queries';
import { ESystemType } from '@src/emp-3.0/types';
import { WorkbenchAction } from '@src/emp-3.0/components/DeploymentDrawer/typed';
import { AxiosError } from 'axios';

export const GET_MAPPED_EVENTS_QUERY_KEY = 'getMappedEvents';
export const GET_MAPPED_STATUS_EVENT_QUERY_KEY = 'getMappedStatusEvent';

export const GET_PRESET_LIST_KEY = 'getPresetList';

export const BLOCK_EVENTS_QUERY_KEY = 'getBlockEvents';
export const GET_BLOCK_VIDEO_EVENTS_QUERY_KEY = 'getBlockVideoEvents';

export const GET_NETWORK_CONFIGURES = 'getNetworkConfigures';
export const GET_CLICK_TRACKING = 'getClickTracking';

export const GET_ALL_DATA_TRANSFORM = 'getAllDataTransform';
export const GET_DATA_TRANSFORM = 'getDataTransform';

export const GET_MAPPED_EVENTS_MANAGEMENT_LIST = 'getMappedEventManagementList';
export const GET_MAPPED_EVENT_DETAILS = 'getMappedEventDetail';
export const GET_MAPPED_EVENT_BY_ID = 'getMappedEventById';

export const GET_DEPLOY_LIST = 'getDeployCandidateList';
export const GET_WORKBENCH_LATEST_DEPLOY_INFO = 'getWorkbenchLatestDeployInfo';
export const GET_WORKBENCH_DEPLOY_AVAILABLE_INTERVAL = 'getWorkbenchDeployAvailableInterval';
export const GET_VALIDATION_INFO = 'get_validation_info';

export const GET_SESSION_REPLAY = 'getSessionReplay';
export const GET_DEPLOY_TASKS = 'getDeployTasks';

const emptyArray = [];

/**
 * through post method to get dimension table data
 * because react-query can not cache the post method, so we need realize the logic by ourselves
 * use all params to create cache key
 * use cache key and queryData object to cache data and update time
 * the default expire time is 5 mins
 * use refetch param to force to refetch data
 *
 * if refecth is true:  refetch data and clear all cache data (the data may be not consistent with the backend this time)
 * if the data is expired:  refetch
 * if has no cache data: fetch data
 */
let queryData: { [key: string]: { value: any; updateTime: number } } = {}; // cache dimension data

const usePostDimensionData = () => {
  const common = useSelector(commonSelector);
  const queryBody: DimensionQueryBody = {
    queries: [
      {
        fields: [METRIC.AppTotalUserEvents],
        interval: `${common?.interval?.from}/${common?.interval?.to}`,
        filters: common?.appliedFilters?.[0]?.rule ?? [],
        limit: 500,
        sortBy: { field: [METRIC.AppTotalUserEvents], order: sortByType.DESC },
      },
    ],
  };
  return useMutation(async (payload: any) => {
    const query = lodash.cloneDeep(queryBody);
    query.queries[0].fields = [...query.queries[0].fields, ...payload.fields];
    if (payload.filters) {
      payload.filters = removeEmptyFilters(payload.filters);
      query.queries[0].filters = [...payload.filters];
    }
    if (payload.interval) {
      query.queries[0].interval = payload.interval;
    }
    const { fields, sortBy, interval, filters } = query.queries[0];
    const key = `${JSON.stringify(fields)}${interval}${JSON.stringify(sortBy)}${JSON.stringify(
      filters,
    )}`;
    const { refetch } = payload;
    if (queryData[key] && !refetch && Date.now() - queryData[key].updateTime < 5 * 60 * 1000) {
      return queryData[key].value;
    } else if (refetch) {
      queryData = {};
    }

    const res = getDimensionData(payload?.params, query)
      .then((res) => {
        trackQueryInfo({ duration: res?.data.duration, fields: fields, query: query });
        return res?.data;
      })
      .catch((error) => {
        trackQueryInfo({ error, fields: fields, query: query });
      });
    queryData[key] = { value: res, updateTime: Date.now() };
    return res;
  });
};

const useGetEventCategory = () => {
  return useQuery(
    ['getEventCategory'],
    async () => {
      const res = await getEventCategory();
      return res?.data;
    },
    { refetchOnMount: false },
  );
};

const useGetMappedEvents = (
  params?: IGetMappedEvents,
  queryOptions?: UseQueryOptions<any, any, any, any>,
) => {
  return useQuery(
    [GET_MAPPED_EVENTS_QUERY_KEY, params],
    async () => {
      const res = await getMappedEvents(params);
      return res?.data;
    },
    { refetchOnMount: false, ...queryOptions },
  );
};

const useGetMappedEventsWithOutOfBox = (preferred_version: 'deployed' | 'draft' = 'deployed') => {
  return useGetMappedEvents(
    {
      need_out_of_box: 1,
      preferred_version,
    },
    { refetchOnWindowFocus: 'always' },
  );
};

const useGetMappedEventById = (props?: any) => {
  return useQuery(
    [GET_MAPPED_EVENT_BY_ID, props?.id],
    async () => {
      const res = await getMappedEventById(props.id);
      return res.data;
    },
    {
      cacheTime: 0,
      enabled: !!props?.id,
      onError: (error: any) => {
        message.error(error?.response?.data?.msg);
      },
      ...props?.options,
    },
  );
};

const useGetMappedEventMetadataEventName = () => {
  return useQuery(
    ['getMappedEventMetadataEventName'],
    async () => {
      const res = await getMappedEventMetadataEventName();
      return res?.data;
    },
    { refetchOnMount: false },
  );
};

const useGetMappedStatusEvent = (field: 'event_name' | 'tag_key' | 'tag_value') => {
  return useQuery(
    [GET_MAPPED_STATUS_EVENT_QUERY_KEY, field],
    async () => {
      const res = await getMappedStatusEvent(field);
      return res?.data;
    },
    { refetchOnMount: false },
  );
};

const usePostMappedEvents = () => {
  return useMutation(async (payload: any) => {
    const res = await postMappedEvents(payload);
    refetchMappedEventRelated();
    refetchDimensionRelated();
    return res?.data;
  });
};

const useUpdateMappedEvent = () => {
  return useMutation(async (payload: any) => {
    const { id, mappedEvent } = payload;
    const res = await updateMappedEvent(id, mappedEvent);
    refetchDimensionRelated();
    refetchMappedEventRelated();
    return res?.data;
  });
};

const useDeleteMappedEvent = () => {
  return useMutation(async (id: number | string) => {
    const res = await deleteMappedEvent(id);
    refetchDimensionRelated();
    refetchMappedEventRelated();
    return res?.data;
  });
};

const usePostQuickMap = () => {
  return useMutation(
    async (
      payload: {
        raw_event: string;
        event_name: string;
        event_category: string;
      }[],
    ) => {
      const res = await postQuickMap(payload);
      refetchDimensionRelated();
      refetchMappedEventRelated();
      return res?.data;
    },
  );
};

const usePresetList = () => {
  return useQuery([GET_PRESET_LIST_KEY], async () => {
    const res = await getPresets();
    return res?.data;
  });
};

const useDeployCandidateList = () => {
  const { workbenchMode } = useInitialInfoStore();
  return useQuery([GET_DEPLOY_LIST, workbenchMode], async () => {
    const res = await getDeployCandidateList();
    return res?.data;
  });
};

const useGetWorkbenchLatestDeployInfo = () => {
  return useQuery(
    [GET_WORKBENCH_LATEST_DEPLOY_INFO],
    async () => {
      const res = await getWorkbenchDeployInfo();
      return res?.data?.data;
    },
    {
      staleTime: 0,
      refetchInterval: (data) => {
        // Poll every 5 seconds if deployment is in progress
        if (!data || data?.status === 'running' || data?.status === 'pending') {
          return 5000;
        }
        return 60000;
      },
      refetchOnWindowFocus: false,
    },
  );
};
const useGetWorkbenchDeployAvailableInterval = () => {
  return useQuery(
    [GET_WORKBENCH_DEPLOY_AVAILABLE_INTERVAL],
    async () => {
      const res = await getValidTimeRange(ESystemType.Workbench);
      return res?.data?.data;
    },
    { staleTime: 0, enabled: false },
  );
};

const useDeployListByIds = () => {
  return useMutation(async (props: { resource: string; payload: { ids: number[] } }) => {
    const res = await postDeployList(props?.resource, props?.payload);
    return res?.data;
  });
};

const useDeploy = () => {
  return useMutation(async (payload) => {
    const res = await postDeploy(payload);
    queryClient.invalidateQueries([GET_ALL_DATA_TRANSFORM]);
    queryClient.invalidateQueries([GET_DEPLOY_LIST]);
    refetchMetricRelated();
    refetchDimensionRelated();
    refetchMappedEventRelated();
    refetchFlowRelated();
    return res?.data;
  });
};

const useDeployWorkbench = () => {
  return useMutation(async (payload) => {
    try {
      const res = await postWorkbenchDeploy(payload);
      queryClient.invalidateQueries([GET_ALL_DATA_TRANSFORM]);
      queryClient.invalidateQueries([GET_DEPLOY_LIST]);
      refetchMetricRelated();
      refetchDimensionRelated();
      refetchMappedEventRelated();
      refetchFlowRelated();
      return res?.data;
    } catch (err) {
      throw new Error((err as AxiosError)?.response?.data?.msg ?? '');
    }
  });
};
const useExportWorkbench = () => {
  return useMutation(async (payload) => {
    const res = await exportWorkbenchToProduction(payload);
    return res?.data;
  });
};

const usePostBlockEvents = () => {
  return useMutation(async (payload: { events: IBlockEventData[] }) => {
    const blockEventsPostData = payload?.events?.map((event) => {
      return {
        event_name: event.event_name,
        block_rules: event.block_rules,
        description: event.description,
      };
    });
    const res = await postBlockEvents(blockEventsPostData);
    queryClient.invalidateQueries([BLOCK_EVENTS_QUERY_KEY]);
    return res?.data;
  });
};

const usePostVideoBlockEvents = () => {
  return useMutation(async (payload: { events: IBlockVideoEventData[] }) => {
    const blockEventsPostData = payload?.events?.map((event) => {
      return {
        event_name: event.event_name,
      };
    });
    const res = await postBlockVideoEvents(blockEventsPostData);
    queryClient.invalidateQueries([BLOCK_EVENTS_QUERY_KEY]);
    queryClient.invalidateQueries([GET_BLOCK_VIDEO_EVENTS_QUERY_KEY]);
    return res?.data;
  });
};

const useGetBlockEventById = (event_id: string, options?: UseQueryOptions<any, any, any, any>) => {
  return useQuery(
    [event_id],
    async () => {
      const res = await getBlockEventById(event_id);
      return res?.data;
    },
    {
      onError: (error) => message.error({ content: (error as any)?.message }),
      cacheTime: 0,
      ...options,
    },
  );
};

const useGetBlockEvents = (props?: any) => {
  return useQuery(
    [BLOCK_EVENTS_QUERY_KEY, props?.eventName],
    async () => {
      const res = await getBlockEvents(props?.eventName);
      return res?.data;
    },
    props?.options,
  );
};

const useGetBlockVideoEvents = () => {
  const { userInfo } = useInitialInfoStore();
  const canBlockVideEvents =
    userInfo?.role === 'Admin' && userInfo?.username?.endsWith('@conviva.com');
  return useQuery(
    [GET_BLOCK_VIDEO_EVENTS_QUERY_KEY],
    async () => {
      const res = await getBlockVideoEvents();
      return res?.data;
    },
    {
      enabled: canBlockVideEvents,
    },
  );
};

const useGetBlockEventsName = (props?: any) => {
  const { data, ...otherResults } = useGetBlockEvents(props);
  // The reason for defining [] as a separate variable
  // prevent stack overflow by calling useGetBlockEventsName method too many times.
  const allBlockedEvents = data?.data ?? emptyArray;
  // Prevents a completely new data from being mapped each time, causing components to rerender all the time.
  // Scenario: useEffect relies on cachedBlockedEventNames, and useState is used in useEffect.
  const [cachedBlockedEventNames, setCachedBlockedEventNames] = useState<string[]>([]);

  useEffect(() => {
    const newBlockedEventNames = allBlockedEvents.map((event) => event?.event_name);
    setCachedBlockedEventNames(newBlockedEventNames);
  }, [allBlockedEvents]);

  return { ...otherResults, data: cachedBlockedEventNames };
};

const usePostUnblockEvents = () => {
  return useMutation(async (payload: { events: string[] }) => {
    const res = await postUnblockEvents(payload.events);
    queryClient.invalidateQueries([BLOCK_EVENTS_QUERY_KEY]);
    queryClient.invalidateQueries([GET_BLOCK_VIDEO_EVENTS_QUERY_KEY]);
    return res?.data;
  });
};

const usePostUnblockVideoEvents = () => {
  return useMutation(async (payload: { events: string[] }) => {
    const res = await postUnblockVideoEvents(payload.events);
    queryClient.invalidateQueries([BLOCK_EVENTS_QUERY_KEY]);
    queryClient.invalidateQueries([GET_BLOCK_VIDEO_EVENTS_QUERY_KEY]);
    return res?.data;
  });
};

const useGetAllDataTransform = (
  targetDimension: string,
  option?: UseQueryOptions<any, any, any, any>,
) => {
  return useQuery(
    [GET_ALL_DATA_TRANSFORM, targetDimension],
    async () => {
      const res = await getAllDataTransform(targetDimension);
      return res?.data;
    },
    option,
  );
};

const useGetDataTransform = (nameId: number, option?: UseQueryOptions<any, any, any, any>) => {
  return useQuery(
    [GET_DATA_TRANSFORM, nameId],
    async () => {
      const res = await getDataTransformByNameId(nameId);
      return res?.data;
    },
    option,
  );
};

const usePostDataTransform = () => {
  return useMutation(async (payload: any) => {
    const res = await postDataTransform(payload);
    queryClient.invalidateQueries([GET_ALL_DATA_TRANSFORM]);
    return res?.data;
  });
};

const useUpdateDataTransform = () => {
  return useMutation(async (payload: any) => {
    const res = await updateDataTransform(payload.nameId, payload.queryBody);
    queryClient.invalidateQueries([GET_ALL_DATA_TRANSFORM]);
    queryClient.invalidateQueries([GET_DATA_TRANSFORM]);
    return res?.data;
  });
};

const useDeleteDataTransform = () => {
  return useMutation(async (payload: any) => {
    const res = await deleteDateTransform(payload.nameId);
    queryClient.invalidateQueries([GET_ALL_DATA_TRANSFORM]);
    return res?.data;
  });
};

const useGetAllMappedEventsInManagement = () => {
  return useQuery([GET_MAPPED_EVENTS_MANAGEMENT_LIST], async () => {
    const res = await getMappedEventManagementList();
    return res?.data?.data;
  });
};

const useGetDetailsMappedEvent = (props: any) => {
  return useQuery(
    [GET_MAPPED_EVENT_DETAILS, props],
    async () => {
      const res = await getMappedEventDetail(props?.event_id, props?.version_id);
      return res?.data;
    },
    {
      cacheTime: 0,
      enabled: !!(props?.event_id && props?.version_id),
      ...props?.options,
    },
  );
};

const useGetNetworkConfigures = (props?: any): UseQueryResult<INETWORK_CONFIGURES[], unknown> => {
  return useQuery(
    [GET_NETWORK_CONFIGURES],
    async () => {
      const res = await getNetworkConfigures();
      return res?.data.data;
    },
    props?.options,
  );
};

const useCreateNetworkConfigures = () => {
  return useMutation(async (payload: any) => {
    const res = await postNetworkConfigures(payload.queryBody);
    queryClient.invalidateQueries([GET_NETWORK_CONFIGURES]);
    return res?.data;
  });
};

const useUpdateNetworkConfigures = () => {
  return useMutation(async (payload: any) => {
    const res = await updateNetworkConfigures(payload.id, payload.queryBody);
    queryClient.invalidateQueries([GET_NETWORK_CONFIGURES]);
    return res?.data;
  });
};

const useDeleteNetworkConfigures = () => {
  return useMutation(async (payload: any) => {
    const res = await deleteNetworkConfigures(payload.id);
    queryClient.invalidateQueries([GET_NETWORK_CONFIGURES]);
    return res?.data;
  });
};

const useReorderNetworkConfigures = () => {
  return useMutation(async (orderedIds: string[]) => {
    const res = await reorderNetworkConfigures(orderedIds);
    queryClient.invalidateQueries([GET_NETWORK_CONFIGURES]);
    return res?.data;
  });
};

const usePostClickTracking = () => {
  return useMutation(async (payload: any) => {
    const res = await postClickTracking(payload.queryBody);
    queryClient.invalidateQueries([GET_CLICK_TRACKING]);
    return res?.data;
  });
};

const useGetClickTracking = (props?: any) => {
  return useQuery(
    [GET_CLICK_TRACKING],
    async () => {
      const res = await getClickTrackingData();
      return res?.data.data;
    },
    props?.options,
  );
};

const useGetSessionReplay = (props?: any) => {
  return useQuery(
    [GET_SESSION_REPLAY],
    async () => {
      const res = await getSessionReplayData();
      return res?.data.data;
    },
    props?.options,
  );
};

const usePostSessionReplay = () => {
  return useMutation(async (payload: any) => {
    const res = await postSessionReplay(payload.queryBody);
    queryClient.invalidateQueries([GET_SESSION_REPLAY]);
    return res?.data;
  });
};

const useDeleteCollectAttr = () => {
  return useMutation(async (payload: any) => {
    const res = await deleteCollectAttr(payload.queryBody);
    queryClient.invalidateQueries([GET_CLICK_TRACKING]);
    return res?.data;
  });
};

const useGetDeployTasks = (params: { page: number; limit: number } = { page: 0, limit: 10 }) => {
  return useQuery(
    [GET_DEPLOY_TASKS, params],
    async () => {
      const res = await getDeployTasks(params);
      return res?.data?.data;
    },
    {
      refetchInterval: (data) => {
        const lastStatus = data?.[0]?.status?.toLowerCase();
        if (lastStatus === 'success' || lastStatus === 'failed') {
          return 30000;
        }
        return 5000;
      },
    },
  );
};

export {
  usePostDimensionData,
  useGetMappedEventById,
  useGetMappedEvents,
  useGetMappedEventsWithOutOfBox,
  usePostMappedEvents,
  useUpdateMappedEvent,
  useDeleteMappedEvent,
  usePostQuickMap,
  useGetEventCategory,
  useGetMappedEventMetadataEventName,
  useGetMappedStatusEvent,
  usePresetList,
  useDeployCandidateList,
  useDeployListByIds,
  useDeploy,
  useDeployWorkbench,
  useExportWorkbench,
  usePostBlockEvents,
  usePostVideoBlockEvents,
  useGetBlockEventById,
  useGetBlockEventsName,
  useGetBlockEvents,
  useGetBlockVideoEvents,
  useGetAllDataTransform,
  useGetDataTransform,
  usePostDataTransform,
  useUpdateDataTransform,
  useDeleteDataTransform,
  usePostUnblockEvents,
  usePostUnblockVideoEvents,
  useGetAllMappedEventsInManagement,
  useGetDetailsMappedEvent,
  useGetNetworkConfigures,
  useCreateNetworkConfigures,
  useUpdateNetworkConfigures,
  useDeleteNetworkConfigures,
  useReorderNetworkConfigures,
  useGetClickTracking,
  usePostClickTracking,
  useDeleteCollectAttr,
  useGetWorkbenchLatestDeployInfo,
  useGetWorkbenchDeployAvailableInterval,
  useGetSessionReplay,
  usePostSessionReplay,
  useGetDeployTasks,
};
